export const PAYMENT_METHODS = {
  credit_card: {
    id: 'credit_card',
    enabled: true,
    label: ''
  },
  bank_transfer: {
    id: 'bank_transfer',
    enabled: true,
    label: ''
  },
  pay_at_store: {
    id: 'pay_at_store',
    enabled: true,
    label: '',
    pickupOnly: true
  },
  corporate_account: {
    id: 'corporate_account',
    enabled: true,
    memberOnly: true,
    label: ''
  }
};

export const PAYMENT_METHOD_IDS = Object.keys(PAYMENT_METHODS);

export const DEFAULT_BANK_TRANSFER = {
  accountName: 'Temizlik Store',
  bankName: 'Ziraat Bankası',
  iban: 'TR12 0001 0002 3456 7890 1234 56'
};

export const DEFAULT_PAYMENT_SETTINGS = {
  defaultMethod: 'credit_card',
  methods: PAYMENT_METHOD_IDS.map((id) => ({ ...PAYMENT_METHODS[id] })),
  bankTransfer: { ...DEFAULT_BANK_TRANSFER },
  kuveyt: {
    enabled: true,
    environment: 'test',
    configured: false,
    passwordSet: false
  }
};

export const normalizePaymentSettings = (raw = {}) => {
  const configured = Array.isArray(raw?.methods) ? raw.methods : [];
  const methods = PAYMENT_METHOD_IDS.map((id) => {
    const fallback = PAYMENT_METHODS[id];
    const match = configured.find((item) => item?.id === id);
    return {
      id,
      enabled: match?.enabled !== false,
      memberOnly: match?.memberOnly !== undefined ? Boolean(match.memberOnly) : Boolean(fallback.memberOnly),
      label: String(match?.label || '').trim()
    };
  });

  const bankTransfer = {
    accountName: String(raw?.bankTransfer?.accountName || DEFAULT_BANK_TRANSFER.accountName).trim(),
    bankName: String(raw?.bankTransfer?.bankName || DEFAULT_BANK_TRANSFER.bankName).trim(),
    iban: String(raw?.bankTransfer?.iban || DEFAULT_BANK_TRANSFER.iban).trim()
  };

  const kuveytRaw = raw?.kuveyt || {};
  const kuveyt = {
    enabled: kuveytRaw.enabled !== false,
    environment: ['prod', 'production', 'live'].includes(String(kuveytRaw.environment || '').toLowerCase())
      ? 'prod'
      : 'test',
    merchantId: String(kuveytRaw.merchantId || '').trim(),
    customerId: String(kuveytRaw.customerId || '').trim(),
    userName: String(kuveytRaw.userName || '').trim(),
    password: String(kuveytRaw.password || '').trim(),
    callbackBaseUrl: String(kuveytRaw.callbackBaseUrl || '').trim(),
    passwordSet: Boolean(kuveytRaw.passwordSet),
    configured: Boolean(kuveytRaw.configured)
  };

  return {
    defaultMethod: PAYMENT_METHOD_IDS.includes(raw?.defaultMethod) ? raw.defaultMethod : 'credit_card',
    methods,
    bankTransfer,
    kuveyt
  };
};

export const hasCorporateInvoiceInfo = (info = {}) => {
  const customerCompany = String(info.customerCompany || '').trim();
  const taxNumber = String(info.taxNumber || info.taxInfo?.taxNumber || '').replace(/\s/g, '');
  const taxOffice = String(info.taxOffice || info.taxInfo?.taxOffice || '').trim();
  return Boolean(customerCompany && taxNumber && taxOffice);
};

export const canUseCorporateAccount = ({ isMember = false, ...invoice } = {}) =>
  Boolean(isMember) && hasCorporateInvoiceInfo(invoice);

export const getResolvedPaymentMethods = (
  paymentSettings,
  {
    isMember = false,
    deliveryMethod = null,
    hasCorporateInvoice = false,
    showLockedCorporate = false
  } = {}
) => {
  const settings = normalizePaymentSettings(paymentSettings);
  return settings.methods
    .filter((method) => {
      if (!method.enabled) return false;
      if (method.memberOnly && !isMember) return false;
      const pickupOnly = Boolean(PAYMENT_METHODS[method.id]?.pickupOnly);
      if (pickupOnly && deliveryMethod && deliveryMethod !== 'pickup') return false;
      if (method.id === 'corporate_account' && !hasCorporateInvoice && !showLockedCorporate) {
        return false;
      }
      return true;
    })
    .map((method) => ({
      ...method,
      locked: method.id === 'corporate_account' && !hasCorporateInvoice
    }));
};

export const getPaymentMethodMeta = (methodId, paymentSettings) => {
  const settings = normalizePaymentSettings(paymentSettings);
  return settings.methods.find((method) => method.id === methodId) || PAYMENT_METHODS[methodId] || null;
};

export const getActivePaymentMethodIds = (paymentSettings, options = {}) =>
  getResolvedPaymentMethods(paymentSettings, options).map((method) => method.id);

export const paymentRequiresOnlineCompletion = (methodId) => methodId === 'credit_card';

export const formatPaymentNote = ({ method, status, reference, deliverySettings }) => {
  const lines = [];
  const labelMap = {
    credit_card: 'Kredi/Banka Kartı',
    bank_transfer: 'Havale / EFT',
    pay_at_store: 'Mağazada Öde',
    corporate_account: 'Kurumsal Cari Hesap'
  };
  lines.push(`Ödeme: ${labelMap[method] || method}`);
  lines.push(`Ödeme durumu: ${status === 'paid' ? 'Ödendi' : 'Ödeme bekleniyor'}`);
  if (reference) {
    lines.push(`Ödeme referansı: ${reference}`);
  }
  return lines.join('\n');
};

export const maskCardNumber = (value = '') => String(value).replace(/\D/g, '').slice(0, 16);

export const formatCardNumber = (value = '') => {
  const digits = maskCardNumber(value);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

export const isCardFormComplete = (card = {}) => {
  const number = maskCardNumber(card.number);
  const expiry = String(card.expiry || '').trim();
  const cvv = String(card.cvv || '').replace(/\D/g, '');
  const holder = String(card.holder || '').trim();
  return number.length === 16 && /^\d{2}\/\d{2}$/.test(expiry) && cvv.length >= 3 && holder.length >= 3;
};
